<script setup lang="ts">
import { Head, usePage } from '@inertiajs/vue3';
import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';

const props = defineProps<{
    headTitle: MaybeRefOrGetter<string>;
    metaDescription: MaybeRefOrGetter<string>;
    ogTitle: MaybeRefOrGetter<string>;
    ogDescription: MaybeRefOrGetter<string>;
    absolutePageUrl: MaybeRefOrGetter<string>;
    ogLocale: MaybeRefOrGetter<string>;
    ogLocaleAlternate: MaybeRefOrGetter<string>;
    jsonLd: MaybeRefOrGetter<string>;
    siteDisplayName: MaybeRefOrGetter<string>;
    jsonLdHeadKey: string;
}>();

const page = usePage();

const headTitle = computed(() => toValue(props.headTitle));
const metaDescription = computed(() => toValue(props.metaDescription));
const ogTitle = computed(() => toValue(props.ogTitle));
const ogDescription = computed(() => toValue(props.ogDescription));
const absolutePageUrl = computed(() => toValue(props.absolutePageUrl));
const ogLocale = computed(() => toValue(props.ogLocale));
const ogLocaleAlternate = computed(() => toValue(props.ogLocaleAlternate));
const jsonLd = computed(() => toValue(props.jsonLd));
const siteDisplayName = computed(() => toValue(props.siteDisplayName));

const brandOgImageUrl = computed(() => {
    const raw = page.props.brandOgImageUrl;

    return typeof raw === 'string' && raw.length > 0 ? raw : null;
});

const brandOgImageWidth = computed(() => {
    const raw = page.props.brandOgImageWidth;

    return typeof raw === 'number' && raw > 0 ? raw : null;
});

const brandOgImageHeight = computed(() => {
    const raw = page.props.brandOgImageHeight;

    return typeof raw === 'number' && raw > 0 ? raw : null;
});

const brandOgImageAlt = computed(() => {
    const raw = page.props.brandOgImageAlt;

    return typeof raw === 'string' && raw.length > 0 ? raw : null;
});

const twitterCard = computed(() =>
    brandOgImageUrl.value ? 'summary_large_image' : 'summary',
);
</script>

<template>
    <Head :title="headTitle">
        <meta
            head-key="description"
            name="description"
            :content="metaDescription"
        />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" :content="absolutePageUrl" />
        <meta head-key="og:title" property="og:title" :content="ogTitle" />
        <meta
            head-key="og:description"
            property="og:description"
            :content="ogDescription"
        />
        <meta
            head-key="og:site_name"
            property="og:site_name"
            :content="siteDisplayName"
        />
        <meta head-key="og:locale" property="og:locale" :content="ogLocale" />
        <meta
            head-key="og:locale:alternate"
            property="og:locale:alternate"
            :content="ogLocaleAlternate"
        />
        <template v-if="brandOgImageUrl">
            <meta
                head-key="og:image"
                property="og:image"
                :content="brandOgImageUrl"
            />
            <meta
                v-if="brandOgImageWidth"
                head-key="og:image:width"
                property="og:image:width"
                :content="String(brandOgImageWidth)"
            />
            <meta
                v-if="brandOgImageHeight"
                head-key="og:image:height"
                property="og:image:height"
                :content="String(brandOgImageHeight)"
            />
            <meta
                v-if="brandOgImageAlt"
                head-key="og:image:alt"
                property="og:image:alt"
                :content="brandOgImageAlt"
            />
            <meta
                head-key="twitter:image"
                name="twitter:image"
                :content="brandOgImageUrl"
            />
        </template>
        <meta
            head-key="twitter:card"
            name="twitter:card"
            :content="twitterCard"
        />
        <meta
            head-key="twitter:title"
            name="twitter:title"
            :content="ogTitle"
        />
        <meta
            head-key="twitter:description"
            name="twitter:description"
            :content="ogDescription"
        />
        <script
            type="application/ld+json"
            :head-key="jsonLdHeadKey"
            v-html="jsonLd"
        />
    </Head>
</template>
